/**
 * Edit this file first. Nearly all visible portfolio content lives here.
 */

export type IconName =
  | "arrow"
  | "code"
  | "download"
  | "external"
  | "github"
  | "linkedin"
  | "mail"
  | "map-pin"
  | "menu"
  | "moon"
  | "play"
  | "sun"
  | "x";

export interface NavigationItem {
  label: string;
  href: string;
}

export interface SocialLink {
  label: string;
  href: string;
  icon: IconName;
}

export interface ProjectLink {
  label: string;
  href: string;
  icon?: IconName;
}

export interface ProjectImage {
  src: string;
  alt: string;
  /**
   * Turns the slide into a click-to-play YouTube embed. `src` is the poster
   * shown until the viewer presses play, so nothing loads from YouTube first.
   */
  youtubeId?: string;
}

export interface StoreLink {
  platform: "ios" | "android";
  href?: string;
  /** `false` renders a disabled button — the title is no longer downloadable. */
  available?: boolean;
}

export interface Project {
  title: string;
  /** Short name for the project tab row. Falls back to `title`. */
  tabLabel?: string;
  category: string;
  /** Shipping state, shown as a dotted pill beside the category. */
  status?: "Released" | "Live" | "In Progress";
  role: string;
  /** A single paragraph, or several rendered as a stack. */
  description: string | string[];
  /** Cover art. Also the only slide when `images` is omitted. */
  image: string;
  imageAlt: string;
  /** Two or more slides turn the artwork panel into a carousel. */
  images?: ProjectImage[];
  /** `contain` pads the panel and shows the whole asset — for icons and key art. */
  imageFit?: "cover" | "contain";
  tags: string[];
  highlights: string[];
  links?: ProjectLink[];
  storeLinks?: StoreLink[];
  storeNote?: string;
}

export interface ExperienceItem {
  role: string;
  organization: string;
  period: string;
  summary: string;
  highlights: string[];
}

export interface SkillGroup {
  title: string;
  skills: string[];
}

/**
 * Routed by Cloudflare Email Routing to the real inbox, so the personal
 * address never appears on the page. Also where the contact form delivers.
 */
const publicEmail = "contact@xfanportfolio.com";

export const siteConfig = {
  name: "Xiao Fan",
  initials: "XF",
  role: "Game & Software Developer",
  location: "Las Vegas, NV",
  email: publicEmail,
  phone: "+1 414 210 1046",
  availability: "Open to game, VR, and software development roles",
  headline: '"My name is Sean.',
  headlineAccent: 'I think, I learn, I code."',
  intro: [
    "I'm a software developer specializing in VR and game development, with five years of Unity and C# experience.",
    "I'm also expanding into full-stack development and AI-assisted workflows, driven by problem-solving and continuous learning.",
  ],
  portrait: {
    src: "/images/xiao-fan-portrait-cutout.webp",
    alt: "Illustrated portrait of Xiao Fan",
  },
  about: [
    "I'm a passionate software developer specializing in VR applications and game development, with five years of hands-on experience using Unity and C#. I'm well versed in version control systems, collaborative development workflows, and production pipelines. Driven by continuous learning, I'm expanding my expertise into areas such as full-stack development and AI-assisted software development.",
    "I thrive on critical thinking, creative problem-solving, and the sense of accomplishment that comes from transforming complex ideas into polished, functional experiences.",
  ],
  aboutTags: ["Unity", "C#", "Meta Quest", "Next.js", "Supabase"],
  navigation: [
    { label: "About", href: "#about" },
    { label: "Skills", href: "#skills" },
    { label: "Experience", href: "#experience" },
    { label: "Projects", href: "#projects" },
  ] satisfies NavigationItem[],
  socialLinks: [
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/in/sean-fan-info/",
      icon: "linkedin",
    },
    { label: "GitHub", href: "https://github.com/FrazeRIP", icon: "github" },
    { label: "Email", href: `mailto:${publicEmail}`, icon: "mail" },
  ] satisfies SocialLink[],
};

/** The mock editor pane that opens the About section. */
export const codePanel = {
  filename: "portfolio.ts",
  entries: [
    ["name", siteConfig.name],
    ["craft", "VR + games"],
    ["approach", "design · ship · maintain"],
  ] satisfies Array<[string, string]>,
  rows: [
    ["Primary stack", "Unity · C# · Meta Quest"],
    ["Also building", "Next.js · Supabase"],
    ["Shipped titles", "Teddy Pop · Raccoon Revenge"],
  ] satisfies Array<[string, string]>,
};

export const projects: Project[] = [
  {
    title: "Magic Candy Shooter",
    category: "Original indie game",
    status: "Released",
    role: "Project Lead · Project Despair",
    description: [
      "A pixel-art brick breaker set in a haunted graveyard, built in Unity and shipped to the web for Halloween 2026.",
      "Aim and fire magic balls at candy bricks, spend candy on more balls, and cash your score in for permanent shop upgrades between runs.",
    ],
    image: "/images/mcs-key.webp",
    imageAlt:
      "Magic Candy Shooter key art — Limulady surrounded by ghosts and floating candy",
    images: [
      {
        src: "/images/mcs-key.webp",
        alt: "Key art — Limulady with her teddy, ringed by ghosts and floating candy",
      },
      {
        src: "/images/mcs-trailer.webp",
        alt: "Magic Candy Shooter trailer",
        youtubeId: "9SgRsH_CDNE",
      },
      {
        src: "/images/mcs-title.webp",
        alt: "Title screen — the Magic Candy Shooter logo over a pixel-art graveyard",
      },
      {
        src: "/images/mcs-shot-1.webp",
        alt: "Gameplay — a 468 combo as magic balls tear through a row of candy bricks",
      },
      {
        src: "/images/mcs-shot-2.webp",
        alt: "Limulady's Magic Blast clearing the lowest brick in every column",
      },
      {
        src: "/images/mcs-shot-3.webp",
        alt: "A cleared board rewarding a Magical! callout and dropped candy pickups",
      },
      {
        src: "/images/mcs-shot-4.webp",
        alt: "A late wave with the board filling up with numbered candy bricks",
      },
      {
        src: "/images/mcs-shop.webp",
        alt: "The shop — permanent upgrades like Lucky Charm, Candy Stash and Swift Spell",
      },
    ],
    tags: ["Unity", "C#", "WebGL", "Gameplay", "Game Design", "UI"],
    highlights: [
      "Designed and built the full run loop — wave scaling, combo scoring, candy economy, and a recharging character skill.",
      "Shipped a WebGL build with a meta-progression shop and English, Simplified Chinese and Japanese localisation.",
    ],
    links: [
      {
        label: "Play on itch.io",
        href: "https://projectdespair.itch.io/magic-candy-shooter",
      },
      {
        label: "Trailer",
        href: "https://www.youtube.com/watch?v=9SgRsH_CDNE",
      },
    ],
  },
  {
    title: "Vampine : New Order",
    tabLabel: "Vampine",
    category: "Original indie game demo",
    status: "Released",
    role: "Project Lead · Project Despair",
    description: [
      "A turn-based strategy roguelike deckbuilder where you lead a vampire rebellion against the Intelligences that now rule the world.",
      "Bond powerful servants, craft tactical card decks, and pair Vampine's many forms with a growing roster to build a deck that is yours alone.",
      "The demo is an early preview built to prove the core gameplay loop — out now on Steam and itch.io while the full game is still in development.",
    ],
    image: "/images/vno-key.webp",
    imageAlt:
      "Vampine : New Order key art — Vampine at the centre of a summoning circle, ringed by bats",
    images: [
      {
        src: "/images/vno-key.webp",
        alt: "Key art — Vampine at the centre of a summoning circle, ringed by bats",
      },
      {
        src: "/images/vno-trailer.webp",
        alt: "Vampine : New Order demo trailer",
        youtubeId: "a1nTwWMc9xY",
      },
      {
        src: "/images/vno-night-lord.webp",
        alt: "Combat — a Round 1 encounter with the card hand and action points laid out",
      },
      {
        src: "/images/vno-rebellion.webp",
        alt: "The servant line-up, pairing Vampine's powers with recruited humans",
      },
      {
        src: "/images/vno-combinations.webp",
        alt: "The card roster — dozens of servant and Vampine form cards",
      },
      {
        src: "/images/vno-intelligences.webp",
        alt: "The Intelligences — the bosses standing between Vampine and the world",
      },
      {
        src: "/images/vno-fantasies.webp",
        alt: "Other Fantasies encountered along the way — a werewolf, a red-hooded hunter and a doll",
      },
    ],
    tags: [
      "Unity",
      "C#",
      "Roguelike",
      "Deckbuilder",
      "Gameplay",
      "Game Design",
      "UI",
      "AI Assist",
    ],
    highlights: [
      "Designed and implemented the core gameplay systems in Unity and C# — turn-based combat, deck building, and the servant bonding loop.",
      "Shipped the demo on Steam and itch.io for Windows, in English and Simplified Chinese.",
    ],
    links: [
      {
        label: "Steam",
        href: "https://store.steampowered.com/app/5317250/Vampine_New_Order/",
      },
      {
        label: "Demo on itch.io",
        href: "https://projectdespair.itch.io/vampine-new-order-demo",
      },
      {
        label: "Trailer",
        href: "https://www.youtube.com/watch?v=a1nTwWMc9xY",
      },
      {
        label: "Game page",
        href: "https://projectdespair.com/games/vampine-new-order",
      },
    ],
  },
  {
    title: "Togawa Sakiko - Slay the Spire Mod",
    tabLabel: "Togawa Sakiko Mod",
    category: "Game mod development",
    status: "Released",
    role: "Project Lead · Project Despair",
    description: [
      "A custom character mod for the beloved roguelike deck builder game Slay the Spire.",
      "Built with Java, ModTheSpire and BaseMod — a full playable class with its own cards, relics, mechanics and events.",
    ],
    image: "/images/sakiko-key.webp",
    imageAlt:
      "Togawa Sakiko Mod key art — Sakiko at the bar in the Slay the Spire title screen",
    images: [
      {
        src: "/images/sakiko-key.webp",
        alt: "Togawa Sakiko Mod key art — custom Slay the Spire title screen",
      },
      {
        src: "/images/sakiko-1.webp",
        alt: "Character select — Togawa Sakiko with her starting relic Monochrome Hairband",
      },
      {
        src: "/images/sakiko-2.webp",
        alt: "The custom card pool — attacks, skills and powers",
      },
      {
        src: "/images/sakiko-3.webp",
        alt: "Card detail — MementoMori, with custom Purge and Divinity keywords",
      },
      {
        src: "/images/sakiko-4.webp",
        alt: "Custom event — Choose your Destiny, with branching outcomes",
      },
      {
        src: "/images/sakiko-5.webp",
        alt: "Combat — Sakiko against a custom boss encounter",
      },
      {
        src: "/images/sakiko-6.webp",
        alt: "Ending illustration — Sakiko at the piano and on stage",
      },
    ],
    tags: ["Java", "Slay the Spire", "Gameplay", "Game Design"],
    highlights: [
      "Brand new character with unique mechanics.",
      "Custom arts, full audio, and special ending.",
    ],
    links: [
      {
        label: "Steam Workshop",
        href: "https://steamcommunity.com/sharedfiles/filedetails/?id=3359249284",
      },
    ],
  },
  {
    title: "FNF : Late Night City Tales",
    tabLabel: "Late Night City Tales",
    category: "Game mod development",
    status: "Released",
    role: "Project Lead · Daytimefish Studio",
    description: [
      "Game mod for the existing rhythm game Friday Night Funkin'.",
      "Introducing a completely original character, 5 custom songs, a complete story-rich gameplay, and a hidden secret…",
    ],
    image: "/images/fnf-late-night-city-tales.webp",
    imageAlt:
      "Late Night City Tales Chapter 1 key art — a character in front of a full moon over a city skyline",
    images: [
      {
        src: "/images/fnf-late-night-city-tales.webp",
        alt: "Late Night City Tales Chapter 1 key art",
      },
      { src: "/images/lnct-keyart-2.webp", alt: "Chapter 1 alternate key art" },
      {
        src: "/images/lnct-gameplay-1.webp",
        alt: "Gameplay — a song in progress with the note highway",
      },
      {
        src: "/images/lnct-gameplay-2.webp",
        alt: "Gameplay — a later song with a different stage",
      },
      {
        src: "/images/lnct-cutscene.webp",
        alt: "Cutscene — characters talking between songs",
      },
    ],
    tags: ["Haxeflixel", "2D", "Gameplay", "Game Design", "Lua", "Haxe"],
    highlights: [
      "Implemented gameplay and chart playback for Chapter 1.",
      "Built stage, dialogue and cutscene flow around the song list.",
    ],
    links: [{ label: "Game page", href: "https://gamebanana.com/mods/397745" }],
  },
  {
    title: "Teddy Pop",
    category: "Shipped mobile title",
    status: "Released",
    role: "Lead Game Programmer · MassDiGI",
    description:
      "A casual bubble pop mobile title led and shipped over a summer studio cycle, from core mechanic design through store release on both platforms.",
    image: "/images/teddy-pop.webp",
    imageAlt: "Teddy Pop title art — a sleeping child holding a teddy bear",
    tags: ["Unity", "C#", "iOS", "Android", "Monetization", "Publish"],
    highlights: [
      "Led programming, ensuring timely delivery and high-quality gameplay.",
      "Developed the core game mechanic, enhancing player engagement.",
      "Implemented art, UI, audio, in-game ads, and in-app purchase features.",
      "Handled publishing, statistics, and debugging.",
    ],
    storeLinks: [
      { platform: "ios", available: false },
      { platform: "android", available: false },
    ],
    storeNote: "No longer available for download",
  },
  {
    title: "Raccoon Revenge",
    category: "Live mobile title",
    status: "Released",
    role: "Game Programmer · MassDiGI",
    description:
      "An existing puzzle-solving mobile title taken over and extended with new content, a new economy, and a rebuilt interface.",
    image: "/images/raccoon-revenge.webp",
    imageAlt:
      "Raccoon Revenge app icon — a raccoon in a sun hat holding a drink",
    imageFit: "contain",
    images: [
      { src: "/images/raccoon-revenge.webp", alt: "Raccoon Revenge app icon" },
      { src: "/images/raccoon-shot-1.webp", alt: "Raccoon Revenge gameplay" },
      { src: "/images/raccoon-shot-2.webp", alt: "Raccoon Revenge gameplay" },
      { src: "/images/raccoon-shot-3.webp", alt: "Raccoon Revenge gameplay" },
      { src: "/images/raccoon-shot-4.webp", alt: "Raccoon Revenge gameplay" },
    ],
    tags: ["Unity", "2D", "C#", "iOS", "Android", "Game Systems"],
    highlights: [
      "Developed new features including additional powerups and traps.",
      "New currency system and star rating system, increasing player retention.",
      "Updated the game's UI to improve usability and visual appeal.",
      "Bug fixing and refactor.",
    ],
    storeLinks: [
      {
        platform: "ios",
        href: "https://apps.apple.com/us/app/raccoon-revenge/id1519100712",
      },
      { platform: "android", available: false },
    ],
    storeNote: "Android build no longer available for download",
  },
  {
    title: "Project Despair — company site",
    tabLabel: "Project Despair",
    category: "Company website",
    status: "Live",
    role: "Owner · Project Despair",
    description: [
      "My own company site, showcasing and hosting all the games and projects I develop.",
      "Built and maintained end to end in Next.js and React.",
    ],
    image: "/images/project-despair-logo.svg",
    imageAlt: "Project Despair logo",
    imageFit: "contain",
    tags: ["Next.js", "React", "Tailwind"],
    highlights: [
      "Built the site that showcases and hosts every project I ship.",
      "Own it end to end — design, build, deployment, and updates.",
    ],
    links: [{ label: "Visit site", href: "https://projectdespair.com/" }],
  },
];

export const experience: ExperienceItem[] = [
  {
    role: "Software Developer",
    organization: "Acadental, Inc — Overland Park, KS",
    period: "March 2022 — Present",
    summary:
      "Build and ship VR applications for Meta Quest devices, and run the web stack that supports them.",
    highlights: [
      "Build, maintain, and publish Meta Quest VR applications using Unity, C#, Visual Studio, Git, Bitbucket, and SourceTree.",
      "Build, deploy, and maintain a Next.js site on a DigitalOcean droplet with Supabase and Medusa V2 ecommerce.",
      "Accelerate development workflows by leveraging Claude Code.",
    ],
  },
  {
    role: "Lead Game Programmer",
    organization: "MassDiGI — Massachusetts",
    period: "January 2020 — February 2021",
    summary:
      "Led programming on a summer studio team, shipping one new title and maintaining another.",
    highlights: [
      'Led programming for the shipped title "Teddy Pop", ensuring timely delivery and high-quality gameplay.',
      "Designed and developed core mechanics, UI, audio, ads, and in-app purchases.",
      'Maintained "Raccoon Revenge" with new powerups, traps, and a currency and star rating system.',
    ],
  },
  {
    role: "Game Programming, B.A.",
    organization: "Becker College — Worcester, MA",
    period: "August 2018 — December 2022",
    summary: "Bachelor's degree in Game Programming.",
    highlights: [],
  },
];

export const skillGroups: SkillGroup[] = [
  {
    title: "Programming",
    skills: [
      "C#",
      "C/C++",
      "Java",
      "JavaScript",
      "HTML",
      "CSS",
      "Lua",
      "Git",
      "Scripting",
      "Claude Code",
      "Codex",
    ],
  },
  {
    title: "Game & XR",
    skills: [
      "Unity",
      "Godot",
      "Meta Quest",
      "VR Platform Development",
      "Game Development",
      "UI",
      "iOS",
      "Android",
      "WebGL",
    ],
  },
  {
    title: "Web & Platforms",
    skills: [
      "Next.js",
      "Supabase",
      "Medusa V2",
      "DigitalOcean",
      "AWS",
      "APIs",
      "iOS",
      "Android",
    ],
  },
  {
    title: "Workflow & Craft",
    skills: [
      "Git",
      "Bitbucket",
      "SourceTree",
      "Visual Studio",
      "Leadership",
      "Photoshop",
      "Illustrator",
    ],
  },
];
